import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "actionsView",
    "addToBoxSetView",
    "createBoxSetView",
    "conflictView",
    "conflictName",
    "conflictBookCount",
    "conflictSuggestion",
    "boxSetSelect",
    "nameInput",
    "duplicateBtn",
    "addToBoxSetBtn",
    "createBoxSetBtn",
    "summary"
  ]

  // onlyDuplicate: hide the "Add to Box Set" / "Create Box Set" actions in
  //   the modal — used on the box-set page, where you're already inside a box
  //   set so duplicate is the only sensible action.
  //
  // external: don't bind our own drag handlers. Another controller (e.g.
  //   box-set-reorder, which owns SortableJS) decides when a duplicate should
  //   happen and calls openDuplicateModal() on us. We just provide the modal
  //   UI + the markDuplicate request.
  static values = {
    onlyDuplicate: { type: Boolean, default: false },
    external:      { type: Boolean, default: false }
  }

  connect() {
    this.source = null
    this.target = null
    if (!this.externalValue) this.bindDragHandlers()
  }

  // Public API for an external driver (box-set-reorder) — open the modal
  // pre-loaded with a single source book and a single target book.
  openDuplicateModal(sourceId, sourceLabel, targetId, targetLabel) {
    this.source = { id: String(sourceId), type: "Book", label: sourceLabel || "this book" }
    this.sources = [ this.source ]
    this.target = { id: String(targetId), type: "Book", label: targetLabel || "this book" }
    this.openActions()
  }

  bindDragHandlers() {
    this.element.querySelectorAll("[data-book-id]").forEach(el => {
      el.draggable = true
      el.addEventListener("dragstart", e => this.onDragStart(e))
      el.addEventListener("dragend", e => this.onDragEnd(e))
      el.addEventListener("dragover", e => this.onDragOver(e))
      el.addEventListener("dragleave", e => this.onDragLeave(e))
      el.addEventListener("drop", e => this.onDrop(e))
    })

    this.element.querySelectorAll("[data-box-set-id]").forEach(el => {
      el.addEventListener("dragover", e => this.onDragOver(e))
      el.addEventListener("dragleave", e => this.onDragLeave(e))
      el.addEventListener("drop", e => this.onDrop(e))
    })
  }

  onDragStart(event) {
    const card = event.currentTarget
    const id = card.dataset.bookId
    const label = card.title || "this book"
    this.source = { id, type: "Book", label }

    // Multi-drag: if select mode is active and the dragged card is selected,
    // include every selected book as a source.
    const sm = this.selectModeController
    if (sm && sm.isActive() && sm.isCardSelected(id)) {
      this.sources = sm.getSelectedIds().map(sid => ({ id: sid, type: "Book" }))
    } else {
      this.sources = [ this.source ]
    }

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", id)
    card.classList.add("opacity-50")
  }

  get selectModeController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "book-select-mode")
  }

  onDragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
  }

  onDragOver(event) {
    if (!this.source) return
    const el = event.currentTarget
    if (el.dataset.bookId === this.source.id) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    el.classList.add("ring-2", "ring-amber-400", "ring-offset-2")
  }

  onDragLeave(event) {
    event.currentTarget.classList.remove("ring-2", "ring-amber-400", "ring-offset-2")
  }

  onDrop(event) {
    event.preventDefault()
    const el = event.currentTarget
    el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2")

    if (!this.source) return

    if (el.dataset.bookId) {
      if (el.dataset.bookId === this.source.id) return
      this.target = { id: el.dataset.bookId, type: "Book", label: el.title || "this book" }
    } else if (el.dataset.boxSetId) {
      this.target = { id: el.dataset.boxSetId, type: "BoxSet", label: el.title || "this box set" }
    } else {
      return
    }

    this.openActions()
  }

  openActions() {
    if (this.sources.length > 1) {
      this.summaryTarget.textContent = `Drop ${this.sources.length} books onto "${this.target.label}"`
    } else {
      this.summaryTarget.textContent = `Drop "${this.source.label}" onto "${this.target.label}"`
    }

    if (this.target.type === "BoxSet") {
      this.duplicateBtnTarget.classList.add("hidden")
      this.createBoxSetBtnTarget.classList.add("hidden")
      this.preselectTargetBoxSet()
    } else if (this.onlyDuplicateValue) {
      // Inside a box set — duplicate is the only action that makes sense.
      // Hide both "Add to Box Set" (we're already in one) and "Create Box
      // Set" (would just spawn a sibling for no reason).
      this.duplicateBtnTarget.classList.remove("hidden")
      if (this.hasAddToBoxSetBtnTarget) this.addToBoxSetBtnTarget.classList.add("hidden")
      this.createBoxSetBtnTarget.classList.add("hidden")
    } else {
      this.duplicateBtnTarget.classList.remove("hidden")
      if (this.hasAddToBoxSetBtnTarget) this.addToBoxSetBtnTarget.classList.remove("hidden")
      this.createBoxSetBtnTarget.classList.remove("hidden")
    }

    this.actionsViewTarget.classList.remove("hidden")
    this.addToBoxSetViewTarget.classList.add("hidden")
    this.createBoxSetViewTarget.classList.add("hidden")
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  preselectTargetBoxSet() {
    if (!this.hasBoxSetSelectTarget) return
    const option = Array.from(this.boxSetSelectTarget.options).find(o => o.value === this.target.id)
    if (option) this.boxSetSelectTarget.value = this.target.id
  }

  cancel(event) {
    if (event && event.target !== this.modalTarget && !event.currentTarget.dataset.action?.includes("cancel")) return
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this.source = null
    this.target = null
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) {
      this.cancel(event)
    }
  }

  showAddToBoxSet() {
    this.actionsViewTarget.classList.add("hidden")
    this.addToBoxSetViewTarget.classList.remove("hidden")
  }

  showCreateBoxSet() {
    this.actionsViewTarget.classList.add("hidden")
    this.createBoxSetViewTarget.classList.remove("hidden")
    setTimeout(() => this.nameInputTarget.focus(), 50)
  }

  back() {
    this.actionsViewTarget.classList.remove("hidden")
    this.addToBoxSetViewTarget.classList.add("hidden")
    this.createBoxSetViewTarget.classList.add("hidden")
    if (this.hasConflictViewTarget) this.conflictViewTarget.classList.add("hidden")
    this.pendingConflict = null
  }

  async markDuplicate() {
    let r
    if (this.sources.length > 1) {
      r = await fetch("/app/books/mark_duplicates_bulk", {
        method: "PATCH",
        headers: this.jsonHeaders(),
        body: JSON.stringify({
          duplicate_of_id: this.target.id,
          source_ids: this.sources.map(s => s.id)
        })
      })
    } else {
      r = await fetch(`/app/books/${this.source.id}/mark_duplicate`, {
        method: "PATCH",
        headers: this.jsonHeaders(),
        body: JSON.stringify({ duplicate_of_id: this.target.id })
      })
    }
    if (r.ok) window.location.reload()
  }

  async confirmAddToBoxSet() {
    const boxSetId = this.boxSetSelectTarget.value
    if (!boxSetId) return
    await this.addBooksToBoxSet(boxSetId, this.bookIdsForCurrentDrop())
    window.location.reload()
  }

  async confirmCreateBoxSet() {
    const name = this.nameInputTarget.value.trim()
    if (!name) return

    const createR = await fetch("/app/box_sets", {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ box_set: { name }, detect_conflict: true })
    })

    // 409 = name already exists. Show the conflict choice instead of erroring.
    if (createR.status === 409) {
      const data = await createR.json()
      this.showConflictView(data)
      return
    }

    if (!createR.ok) {
      let message = `Couldn't create box set (HTTP ${createR.status}).`
      try {
        const errJson = await createR.json()
        if (errJson && errJson.errors) {
          message = `Couldn't create box set: ${errJson.errors.join(", ")}`
        }
      } catch {}
      alert(message)
      return
    }

    const json = await createR.json()
    await this.addBooksToBoxSet(json.id, this.bookIdsForCurrentDrop())
    window.location.reload()
  }

  // Conflict view — populated and shown when the server detects a name clash.
  showConflictView(data) {
    this.pendingConflict = data // remember so merge/rename actions know what to do
    this.conflictNameTarget.textContent = data.existing.name
    this.conflictBookCountTarget.textContent = data.existing.book_count
    this.conflictSuggestionTarget.textContent = data.suggested
    this.createBoxSetViewTarget.classList.add("hidden")
    this.conflictViewTarget.classList.remove("hidden")
  }

  // User chose: merge into the existing box set with the same name.
  async mergeIntoExisting() {
    if (!this.pendingConflict) return
    await this.addBooksToBoxSet(this.pendingConflict.existing.id, this.bookIdsForCurrentDrop())
    window.location.reload()
  }

  // User chose: use the auto-suggested alternative name (e.g. "Law Of Success 2").
  async useAlternativeName() {
    if (!this.pendingConflict) return
    this.nameInputTarget.value = this.pendingConflict.suggested
    this.pendingConflict = null
    this.conflictViewTarget.classList.add("hidden")
    this.createBoxSetViewTarget.classList.remove("hidden")
    this.confirmCreateBoxSet()
  }

  // User chose: go back to the create form to type a totally different name.
  backToCreate() {
    this.pendingConflict = null
    this.conflictViewTarget.classList.add("hidden")
    this.createBoxSetViewTarget.classList.remove("hidden")
    setTimeout(() => this.nameInputTarget.focus(), 50)
  }

  bookIdsForCurrentDrop() {
    // Sources are always Books. Target may be a Book (include too) or a BoxSet (only sources).
    const ids = this.sources.map(s => s.id)
    if (this.target.type === "Book") ids.push(this.target.id)
    return [ ...new Set(ids) ]
  }

  async addBooksToBoxSet(boxSetId, bookIds) {
    return fetch(`/app/box_sets/${boxSetId}/add_books`, {
      method: "PATCH",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ book_ids: bookIds })
    })
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      "Accept": "application/json"
    }
  }
}
